import { ArrowRight, BookOpen, Code2, FileJson, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

import { useHandbook } from './HandbookContext';
import { GROUP_ICONS, HandbookSearch } from './HandbookSearch';
import { pagePath, pagesInGroup, resolveHandbookHref } from './handbookContent';

export function HandbookHome() {
  const handbook = useHandbook();
  const integrationPages = pagesInGroup(handbook, 'integrations');
  const otherGroups = handbook.groups.filter((group) => !group.featured);
  const totalMinutes = handbook.pages.reduce((sum, page) => sum + page.minutes, 0);
  const builder = resolveHandbookHref('/integrations/builder');

  return (
    <div>
      <section className="relative z-10 rounded-2xl border border-border bg-card/40 px-8 py-12">
        <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl" aria-hidden="true">
          <div className="absolute inset-0 opacity-60 [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:18px_18px] [mask-image:linear-gradient(to_bottom,black,transparent)]" />
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
        </div>
        <div className="relative max-w-2xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs text-muted-foreground">
            <BookOpen className="h-3.5 w-3.5 text-brand" />
            Studio Handbook
          </div>
          <h1 className="text-4xl font-bold tracking-tight text-foreground">How Studio works, and how to extend it.</h1>
          <p className="mt-4 text-lg leading-8 text-muted-foreground">
            The architecture behind every generated app, the MicroVM workspaces they are built in, and step-by-step guides for
            bringing new services into Studio as integrations.
          </p>
          <div className="mt-8 max-w-xl">
            <HandbookSearch large />
          </div>
          <div className="mt-4 text-xs text-muted-foreground">
            {handbook.pages.length} guides · about {Math.round(totalMinutes / 5) * 5} minutes end to end
          </div>
        </div>
      </section>

      <section className="mt-10 overflow-hidden rounded-2xl border border-brand/30 bg-gradient-to-br from-brand-muted/40 via-card/40 to-card/40">
        <div className="grid gap-8 p-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <div>
            <div className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-brand">
              <Sparkles className="h-3.5 w-3.5" />
              Integrations
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-foreground">Bring any service into Studio</h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Integrations let generated apps send messages, call models, look up records and more — while RapidSOS keeps the
              credentials, meters the usage and shows the price. Build one from an OpenAPI spec without code, or write a typed SDK
              adapter when you need full control.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild>
                <a href={builder.internal ? builder.to : builder.href} target="_blank" rel="noreferrer">
                  <FileJson className="mr-2 h-4 w-4" />
                  Open the Integration Builder
                </a>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/integrations/sdk-tutorial">
                  <Code2 className="mr-2 h-4 w-4" />
                  SDK tutorial
                </Link>
              </Button>
            </div>
          </div>
          <ul className="divide-y divide-border rounded-xl border border-border bg-background/60">
            {integrationPages.map((page) => (
              <li key={page.slug}>
                <Link to={pagePath(page)} className="group flex items-center gap-3 px-4 py-3 transition-colors hover:bg-secondary/40">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground">{page.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">{page.summary}</span>
                  </span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{page.minutes} min</span>
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-brand" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-10 grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {otherGroups.map((group) => {
          const Icon = GROUP_ICONS[group.id];
          const pages = pagesInGroup(handbook, group.id);
          return (
            <div key={group.id} className="flex flex-col rounded-2xl border border-border bg-card/40 p-6">
              <div className="mb-4 flex h-9 w-9 items-center justify-center rounded-lg bg-secondary">
                <Icon className="h-4 w-4" />
              </div>
              <h3 className="text-base font-semibold text-foreground">{group.label}</h3>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{group.blurb}</p>
              <ul className="mt-4 flex-1 space-y-1">
                {pages.map((page) => (
                  <li key={page.slug}>
                    <Link to={pagePath(page)} className="group flex items-center justify-between gap-2 rounded-md py-1 text-sm text-muted-foreground hover:text-foreground">
                      {page.title}
                      <ArrowRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>
    </div>
  );
}
